/**
 * Guest-account identity for the signed-in caller.
 *
 * This is the ONE place in the FE where checking a role NAME is correct.
 * Everywhere else the rule holds: gate on `membership.permissions` slugs
 * (see `use-card-permissions.ts`), because a tenant admin can edit any
 * system role's permission set in Settings → Role Management, so a
 * role-derived switch goes silently wrong the moment a tenant customizes
 * one. Guest is the exception because guest-ness IS the role: its
 * restrictions are hard ceilings enforced in BE code keyed off the slug,
 * not positive permission grants, so no permission edit can widen them.
 *
 * What this answers is "should the UI render a control this account can
 * never use" — the designated-channel scope means a guest's conversation
 * list is already filtered server-side, so a browse or create button is
 * not a security hole, just a button whose only outcome is a 403 toast.
 *
 * Fall-open contract, deliberately the INVERSE of `cardPermissionsFromMe`:
 * an unresolved `me` reads NOT-guest, so the shell renders its full nav
 * while `/api/me` is in flight and settles down rather than flashing a
 * reduced rail at every member on every load. The failure mode of a false
 * negative here is a dead control (a 403 toast); a false positive would
 * hide navigation from a paying member.
 */

import { useMemo } from 'react'

import type { Me } from '@/api/types-bridge'
import { useMe } from './use-me'

/** The tenant role slug that carries the guest ceiling. */
export const GUEST_ROLE = 'guest'

export function isGuestMembership(me: Me | undefined): boolean {
  return me?.membership?.role === GUEST_ROLE
}

export function useIsGuest(): boolean {
  const me = useMe()
  return useMemo(() => isGuestMembership(me.data), [me.data])
}
